import React from 'react';

import AutoEnrolIcon from 'icons/auto-enrol.svg';
import CalendarIcon from 'icons/calendar.svg';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import voca from 'voca';

import ActionsPill from 'components/application/ActionsPill';
import TableCell from 'components/application/TableCell';

import { useTabContext } from 'components/contexts/TabContext';

import { formatDate, isAfter } from 'components/helpers/dates';
import { resourceShape } from 'components/helpers/serialisableResources';
import { personDisplayName } from 'components/helpers/users';

export default function BookingsRow({
  bookingConnection,
  bookingSource,
  course,
  onRowClick,
  personnel,
}) {
  const { selectedTabKey } = useTabContext();

  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'bookings.BookingsRow',
  });

  const { t: i18nTCommon } = useTranslation('common');

  const {
    attributes: { createdAt, date },
    relationships: { activeRegistration },
  } = bookingSource;

  const { learningMethod, name, provider } = course.attributes;

  return (
    <tr
      className='hover:tw-bg-grey-025'
      onClick={() => onRowClick(bookingConnection.id)}
    >
      <TableCell>
        <div className='truncated-text-container tw-text-m'>{name}</div>
      </TableCell>
      <TableCell>
        {personnel && bookingSource && bookingConnection && (
          <div className='truncated-text-container tw-text-m'>
            {personnelNamesForRow(personnel, bookingSource, bookingConnection)}
          </div>
        )}
      </TableCell>
      <TableCell>
        <div className='truncated-text-container tw-text-m'>
          {selectedTabKey === 'eLearning' ?
            provider
          : selectedTabKey === 'pastBookings' &&
            learningMethod &&
            i18nTCommon(
              `courses.learningMethods.${voca.camelCase(learningMethod)}`,
            )
          }
        </div>
      </TableCell>
      <TableCell>
        {selectedTabKey === 'eLearning' ?
          <ActionsPill
            color='green'
            displayText={
              activeRegistration?.data?.id ?
                i18nTCommon('shared.inProgress')
              : i18nT('enrolledText', {
                  date: formatDate({ date: createdAt, format: 'd MMM yyyy' }),
                })
            }
            icon={<AutoEnrolIcon height={16} width={16} />}
          />
        : <ActionsPill
            color={
              date && isAfter({ date: new Date(), dateToCompare: date }) ?
                'cyan'
              : 'green'
            }
            displayText={
              date ?
                i18nT('bookedText', {
                  date: formatDate({ date, format: 'd MMM yyyy' }),
                })
              : i18nTCommon('bookings.booked')
            }
            icon={<CalendarIcon height={16} width={16} />}
          />
        }
      </TableCell>
    </tr>
  );
}

function personnelNamesForRow(personnel, bookingSource, bookingConnection) {
  const { t: i18nTCommon } = useTranslation('common');

  const personnelArray = personnel.map((personnel) =>
    personDisplayName({ attributes: personnel.attributes }),
  );

  const totalPersonnelCount =
    bookingConnection.relationships.source.data.type === 'bookingGroup' ?
      bookingSource.meta.personnelCount
    : 1;

  const othersCounts = totalPersonnelCount - personnel.length;

  let personnelNames;
  if (othersCounts >= 1) {
    personnelNames = `${personnelArray.join(', ')} ${i18nTCommon('shared.other', { count: othersCounts })}`;
  } else {
    personnelNames = personnelArray.join(', ');
  }

  return personnelNames;
}

BookingsRow.propTypes = {
  bookingConnection: resourceShape('bookingConnection'),
  bookingSource: PropTypes.oneOfType([
    resourceShape('booking'),
    resourceShape('bookingGroup'),
  ]),
  course: resourceShape('course'),
  onRowClick: PropTypes.func.isRequired,
  personnel: PropTypes.arrayOf(resourceShape('personnel')),
};
